// ../../jam-core-react/backend-hooks.ts
import { useCallback } from "react";

// ../../lib/state-tree-react.js
import React from "react";
import { on as on2, use as useMinimalState } from "use-minimal-state";

// ../../lib/state-tree.js
import { is, on, emit, clear, set } from "minimal-state";

// ../../lib/causal-log.js
var timeout = null;
function log() {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    console.log("\n----------------\n\n");
  }, 20);
  console.log(...arguments);
}

// ../../lib/state-tree.js
var root = {
  children: [],
  level: -1
};
var current = root;
var renderRoot = null;
var nUses = 0;
var renderTime = 0;
var updateSet = new Set();
function use(Component, props, stableProps) {
  let isComponent = typeof Component === "function";
  if (!isComponent) {
    return useExternalState(Component, props);
  }
  let key = props == null ? void 0 : props.key;
  let element = current.children.find(c => c.component === Component && c.key === key);
  let [newElement] = _run(Component, props, {
    element,
    isUsed: true,
    stableProps
  });
  return newElement.fragment[0];
}
function _run(Component, props, _temp) {
  if (props === void 0) {
    props = null;
  }
  let {
    element,
    stableProps = null,
    isUsed = false,
    isEvent = false,
    isMount = false,
    isRoot = false,
    state
  } = _temp === void 0 ? {} : _temp;
  let mustUpdate = updateSet.has(element);
  if (mustUpdate) updateSet.delete(element);
  if (sameProps(element == null ? void 0 : element.props, props) && !mustUpdate) {
    element.renderTime = renderTime;
    if (element.isEvent) {
      resultToFragment(void 0, element.fragment);
    }
    return [element, void 0];
  }
  if (element === void 0) {
    isMount = true;
    element = {
      component: Component,
      render: Component,
      props,
      stableProps,
      key: props == null ? void 0 : props.key,
      children: [],
      uses: [],
      renderTime,
      parent: current,
      level: current.level + 1,
      root: renderRoot,
      isUsed,
      isEvent,
      fragment: Fragment()
    };
    if (isRoot) {
      element.actionSubs = new Map();
      element.stateSubs = new Map();
      element.state = state;
      element.root = element;
      renderRoot = element;
    }
    log2("mounting new element", element.component.name);
    current.children.push(element);
  } else {
    if (props !== null) element.props = props;
    if (stableProps !== null) element.stableProps = stableProps;
    element.renderTime = renderTime;
  }
  let renderProps = {
    ...element.props,
    ...element.stableProps
  };
  let tmp = [current, nUses];
  current = element;
  nUses = 0;
  let result;
  if (isMount) {
    result = Component(renderProps);
    if (typeof result === "function") {
      let mountUses = element.uses;
      element.uses = [];
      nUses = 0;
      element.render = result;
      result = element.render(renderProps);
      element.uses = element.uses.concat(mountUses);
    }
  } else {
    result = element.render(renderProps);
  }
  [current, nUses] = tmp;
  let children = element.children;
  for (let i = children.length - 1; i >= 0; i--) {
    let child = children[i];
    if (child.renderTime !== renderTime) {
      cleanup(child);
      children.splice(i, 1);
    }
  }
  log2("rendered", Component.name, "with result", result);
  let updateKeys = resultToFragment(result, element.fragment);
  return [element, updateKeys];
}
function cleanup(element) {
  var _a2;
  log2("unmounting element", element.component.name);
  clear(element.fragment);
  for (let use6 of element.uses) {
    (_a2 = use6 == null ? void 0 : use6.cleanup) == null ? void 0 : _a2.call(use6);
  }
  if (renderRoot !== null) {
    unsubscribeAll(renderRoot.actionSubs, element);
    unsubscribeAll(renderRoot.stateSubs, element);
  }
  for (let child of element.children) {
    cleanup(child);
  }
}
function sameProps(prevProps, props) {
  if (prevProps === void 0) return false;
  if (prevProps === props) return true;
  if (prevProps === null || props === null || props === void 0) return false;
  for (let key in prevProps) {
    if (!(key in props) || props[key] !== prevProps[key]) return false;
  }
  for (let key in props) {
    if (!(key in prevProps) || props[key] !== prevProps[key]) return false;
  }
  return true;
}
function queueUpdate(caller, reason) {
  if (reason === void 0) {
    reason = "";
  }
  log2("queuing update", caller.component.name, reason);
  markForUpdate(caller);
  return Promise.resolve().then(() => {
    updateAll(caller.component.name + " " + reason);
  });
}
function updateAll(reason) {
  if (updateSet.size === 0) {
    return;
  }
  log2("tree update caused by", reason);
  let orderedElements = [...updateSet].sort((el1, el2) => el1.level - el2.level);
  for (let element of orderedElements) {
    if (!updateSet.has(element)) {
      continue;
    }
    renderRoot = element.root;
    renderTime = Date.now();
    log2("rerendering", element.component.name);
    let [, updateKeys] = _run(element.component, element.props, {
      element
    });
    log2("render took", Date.now() - renderTime);
    let result = element.fragment[0];
    emit(element.fragment, result, updateKeys);
    renderRoot = null;
  }
}
function markForUpdate(element) {
  let parent = element;
  updateSet.add(parent);
  if (!parent.isUsed) return parent;
  while (parent.parent !== root) {
    parent = parent.parent;
    updateSet.add(parent);
    if (!parent.isUsed) return parent;
  }
  return parent;
}
function unsubscribeAll(subscriptions, element) {
  for (let entry of subscriptions) {
    let [key, set2] = entry;
    set2.delete(element);
    if (set2.size === 0) {
      subscriptions.delete(key);
    }
  }
}
function useExternalState(state, keyOrSelector) {
  if (current === root) throw Error("Hooks can only be called during render");
  let caller = current;
  let use6 = caller.uses[nUses];
  let [key, selector] = typeof keyOrSelector === "function" ? [void 0, keyOrSelector] : [keyOrSelector, void 0];
  if (use6 === void 0 || use6.key !== keyOrSelector) {
    if (use6 !== void 0) {
      use6.cleanup();
    } else {
      use6 = {
        key: keyOrSelector
      };
    }
    if (selector !== void 0) {
      try {
        use6.value = selector(state);
      } catch (err) {
        console.warn(err);
      }
    }
    let listener = () => {
      if (current === caller) return;
      if (selector !== void 0) {
        let value = selector(state);
        if (use6.value === value) return;
        use6.value = value;
      }
      queueUpdate(caller, "useExternalState " + key);
    };
    use6.cleanup = key === void 0 ? on(state, listener) : on(state, key, listener);
    caller.uses[nUses] = use6;
  }
  nUses++;
  return key === void 0 ? use6.value : state[key];
}
function Fragment() {
  let fragment = [void 0];
  fragment._frag = true;
  fragment._deps = new Map();
  fragment._type = "nullish";
  on(fragment, function () {
    for (let e of fragment._deps) {
      e[1](...arguments);
    }
  });
  return fragment;
}
function isFragment(thing) {
  return thing == null ? void 0 : thing._frag;
}
function resultToFragment(result, fragment) {
  if (result === void 0 || result === null) {
    fragment._type = "nullish";
    fragment[0] = result;
    return;
  }
  if (result._frag) {
    fragment[0] = result[0];
    fragment._type = result._type;
    result._deps.set(fragment, (value, keys) => {
      fragment[0] = value;
      emit(fragment, value, keys);
    });
    return;
  }
  if (result._atom) {
    fragment[0] = result[0];
    fragment._type = "plain";
    return;
  }
  if (result._merged) {
    let updateKeys = setMergedFragment(fragment, result);
    fragment._type = "merged";
    return updateKeys;
  }
  if (Array.isArray(result) || result instanceof Map || result instanceof Set) {
    fragment[0] = result;
    fragment._type = "plain";
    return;
  }
  if (typeof result === "object") {
    let updateKeys = setObjectFragment(fragment, result);
    fragment._type = "object";
    return updateKeys;
  }
  fragment[0] = result;
  fragment._type = "plain";
}
function setObjectFragment(fragment, obj) {
  let pureObj = {};
  let keys = fragment._type === "object" ? [] : void 0;
  let oldObj = fragment[0];
  fragment[0] = pureObj;
  for (let key in obj) {
    let prop = obj[key];
    if (isFragment(prop)) {
      pureObj[key] = prop[0];
      prop._deps.set(fragment, value => {
        if (pureObj[key] === value) return;
        pureObj[key] = value;
        emit(fragment, pureObj, [key]);
      });
    } else {
      pureObj[key] = prop;
    }
    if (keys !== void 0 && oldObj[key] !== pureObj[key]) {
      keys.push(key);
    }
  }
  return keys;
}
function setMergedFragment(fragment, objArray) {
  let mergedObj = {};
  let oldObj = fragment[0];
  fragment[0] = mergedObj;
  function updateSelf(value, keys2) {
    let objects = objArray.map(oa => oa._frag ? oa[0] : oa);
    fragment[0] = Object.assign(mergedObj, ...objects);
    emit(fragment, fragment[0], keys2 != null ? keys2 : Object.keys(value));
  }
  for (let objFragment of objArray) {
    if (isFragment(objFragment)) {
      Object.assign(mergedObj, objFragment[0]);
      objFragment._deps.set(fragment, updateSelf);
    } else {
      Object.assign(mergedObj, objFragment);
    }
  }
  let keys;
  if (fragment._type === "merged") {
    keys = [];
    for (let key in mergedObj) {
      if (oldObj[key] !== mergedObj[key]) {
        keys.push(key);
      }
    }
  }
  return keys;
}
var _a;
var doLog = !!((_a = window.jamConfig) == null ? void 0 : _a.development);
function log2() {
  if (doLog === false) return;
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  log("STATE-TREE", ...args);
}

// ../../lib/state-tree-react.js
function use2(Component, props, stableProps) {
  let isComponent = typeof Component === "function";
  if (isComponent) {
    return useStateComponent(Component, props, stableProps);
  } else {
    return useMinimalState(Component, props);
  }
}
function useStateComponent(Component, props, stableProps) {
  let [stable] = React.useState({
    shouldRun: true,
    element: null,
    component: Component
  });
  if (props) props.key = stable;else props = {
    key: stable
  };
  if (stable.shouldRun) {
    let {
      component
    } = stable;
    let element = root.children.find(c => c.component === component && c.key === stable);
    [element] = _run(component, props, {
      element,
      stableProps
    });
    stable.element = element;
  }
  stable.shouldRun = true;
  let [, forceUpdate] = React.useState(0);
  React.useEffect(() => {
    let {
      element
    } = stable;
    on2(element.fragment, () => {
      log2("React update", stable.component.name);
      stable.shouldRun = false;
      forceUpdate(n => n + 1);
    });
    return () => {
      cleanup(element);
      let i = root.children.indexOf(element);
      if (i !== -1) root.children.splice(i, 1);
    };
  }, [stable]);
  return stable.element.fragment[0];
}

// ../../jam-core-react/JamContext.tsx
import { use as use3 } from "use-minimal-state";
import React2, { createContext, useContext, useMemo } from "react";
import { createApi, createJam, defaultState } from "jam-core";
var fakeDispatch = async (type, payload) => {
  throw Error("state not created yet");
};
var fakeSetProps = async state => {
  throw Error("state not created yet");
};
var defaultApi = createApi(defaultState, fakeDispatch, fakeSetProps);
var JamContext = /*#__PURE__*/createContext([defaultState, defaultApi]);
function JamProvider(_ref) {
  let {
    children,
    state,
    api,
    options,
    onState
  } = _ref;
  let value = useMemo(() => {
    if (state === void 0) {
      [state, api] = createJam(options);
    }
    onState == null ? void 0 : onState(state);
    return [state, api];
  }, []);
  return /* @__PURE__ */React2.createElement(JamContext.Provider, {
    value
  }, children);
}
function useJam() {
  return useContext(JamContext);
}
function useJamState(keys) {
  let [state] = useContext(JamContext);
  return use3(state, keys);
}

// ../../lib/identity-utils.js
import { toBytes as toBytes2, toBase64 as toBase642 } from "fast-base64/js";
import { toUrl, fromUrl } from "fast-base64/url";

// ../../lib/watsign-records.js
import { sign, verify } from "watsign";
import { toBytes, toBase64 } from "fast-base64";

// ../../lib/util.js
function mergeObject(obj, partialObj) {
  for (let key in partialObj) {
    let value = partialObj[key];
    if (value !== void 0) {
      obj[key] = value;
    }
  }
}
function concatBytes() {
  for (var _len2 = arguments.length, arrays = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    arrays[_key2] = arguments[_key2];
  }
  if (!arrays.length) return null;
  let totalLength = arrays.reduce((acc, value) => acc + value.length, 0);
  let result = new Uint8Array(totalLength);
  let length = 0;
  for (let array of arrays) {
    result.set(array, length);
    length += array.length;
  }
  return result;
}

// ../../lib/watsign-records.js
async function signData(_ref2) {
  let {
    record,
    keypair: keypair2,
    validSeconds,
    validUntil
  } = _ref2;
  let Certified = await toBase64(new TextEncoder().encode(JSON.stringify(record)));
  let Expiration = validUntil || (validSeconds || 1800) + Math.floor(Date.now() / 1e3);
  let bytesToSign = await createBytesToSign({
    Version: 0,
    Expiration,
    KeyType: "ed25519",
    Certified
  });
  let Signatures = [];
  try {
    Signatures.push(await createSignature(bytesToSign, keypair2));
  } catch {}
  return {
    Version: 0,
    Expiration,
    KeyType: "ed25519",
    Certified,
    Signatures
  };
}
async function createSignature(bytesToSign, _ref3) {
  let {
    publicKey,
    secretKey
  } = _ref3;
  let signatureBytes = await sign(bytesToSign, secretKey);
  let signature = await toBase64(signatureBytes);
  return {
    Identity: await toBase64(publicKey),
    Payload: signature
  };
}
async function createBytesToSign(_ref4) {
  let {
    Version,
    Expiration,
    KeyType,
    Certified
  } = _ref4;
  let versionBytes = new Uint8Array(4);
  let expirationBytes = new Uint8Array(8);
  let keyTypeBytes = new TextEncoder().encode(KeyType);
  let payloadBytes = await toBytes(Certified);
  for (let i = 0; i < 4; i++) {
    versionBytes[i] = Version >> 8 * i & 255;
  }
  for (let i = 0; i < 8; i++) {
    expirationBytes[i] = Expiration >> 8 * i & 255;
  }
  return concatBytes(versionBytes, expirationBytes, keyTypeBytes, payloadBytes);
}

// ../../lib/identity-utils.js
var MESSAGE_VALIDITY_SECONDS = 300;
async function signData2(identity, data) {
  return signData({
    record: data,
    keypair: keypair(identity),
    validSeconds: MESSAGE_VALIDITY_SECONDS
  });
}
async function signedToken(identity) {
  let signed = await signData2(identity, {});
  return encode(new TextEncoder().encode(JSON.stringify(signed)));
}
function keypair(identity) {
  return {
    secretKey: decode(identity.secretKey),
    publicKey: decode(identity.publicKey)
  };
}
function decode(base64String) {
  return toBytes2(fromUrl(base64String));
}
function encode(binaryData) {
  return toUrl(toBase642(binaryData));
}

// ../../jam-core-react/backend-hooks.ts
import { apiUrl } from "jam-core";

// ../../lib/GetRequest.js
import { update } from "minimal-state";
function GetRequest(_ref5) {
  let {
    path,
    dontFetch,
    fetchOnMount,
    getToken
  } = _ref5;
  let ourState = "idle";
  let shouldFetch = !!path && !dontFetch;
  if (shouldFetch && fetchOnMount) {
    ourState = "loading";
    getRequest(path, getToken);
  }
  return function GetRequest2(_ref6) {
    let {
      path: path2,
      dontFetch: dontFetch2,
      getToken: getToken2
    } = _ref6;
    var _a2;
    let {
      state,
      data,
      status
    } = (_a2 = use(queryCache, path2)) != null ? _a2 : idleQuery;
    let shouldFetch2 = !!path2 && !dontFetch2;
    let actionRequired = shouldFetch2 && state === "idle";
    if (!shouldFetch2) {
      ourState = "idle";
      return {
        data: null,
        status: null,
        isLoading: false
      };
    }
    if (actionRequired) {
      ourState = "loading";
      getRequest(path2, getToken2);
    } else {
      ourState = state;
    }
    let isLoading = ourState === "loading";
    return {
      data,
      status,
      isLoading
    };
  };
}
async function getRequest(path, getToken) {
  var _a2;
  setCache(path, {
    state: "loading"
  });
  let headers = {
    Accept: "application/json"
  };
  if (getToken) headers.Authorization = `Token ${await getToken()}`;
  let res = await fetch(path, {
    headers
  }).catch(console.warn);
  let {
    state,
    data,
    status
  } = getCache(path);
  if (state !== "loading") {
    return [data, state === "success", status];
  }
  if (res === void 0) {
    setCache(path, idleQuery);
    return [null, false, null];
  }
  status = res.status;
  let ok = status < 400;
  if (ok) {
    state = "success";
    data = (_a2 = await res.json().catch(console.warn)) != null ? _a2 : null;
  } else {
    state = "error";
    data = null;
  }
  setCache(path, {
    state,
    data,
    status
  });
  return [data, ok, status];
}
var queryCache = {};
var idleQuery = {
  state: "idle",
  data: null,
  status: null
};
function getCache(path) {
  var _a2;
  return (_a2 = queryCache[path]) != null ? _a2 : idleQuery;
}
function setCache(path, _ref7) {
  let {
    state,
    data,
    status
  } = _ref7;
  let cached = queryCache[path];
  if (cached === void 0) {
    queryCache[path] = {
      state,
      data: data != null ? data : null,
      status: status != null ? status : null
    };
  } else {
    mergeObject(cached, {
      state,
      data,
      status
    });
  }
  update(queryCache, path);
}

// ../../jam-core-react/backend-hooks.ts
function useApiQuery(path, _temp2) {
  let {
    dontFetch = false,
    fetchOnMount = false
  } = _temp2 === void 0 ? {} : _temp2;
  const [state] = useJam();
  const getToken = useCallback(() => signedToken(state.myIdentity), []);
  let {
    data,
    isLoading,
    status
  } = use2(GetRequest, {
    path: apiUrl() + path,
    dontFetch,
    fetchOnMount,
    getToken
  });
  return [data, isLoading, status];
}

// ../../jam-core-react.ts
import { use as use5 } from "use-minimal-state";

// ../../jam-core-react/components/v1/Participant.jsx
import React4, { useEffect, useState } from "react";

// ../../lib/avatar.js
var DEFAULT_AVATAR = `/img/avatar-default.png`;
var roomAvatar = (info, room, defaultAvatar) => {
  var _a2, _b, _c, _d, _e;
  if ((_a2 = room.userDisplay) == null ? void 0 : _a2.identities) {
    return room.userDisplay.identities[info.id].avatar || defaultAvatar;
  } else if ((_b = room.userDisplay) == null ? void 0 : _b.avatars) {
    return room.userDisplay.avatars[info.id] || defaultAvatar;
  } else if ((_c = room.userDisplay) == null ? void 0 : _c.randomIdentities) {
    return selectFromList(info.id, (_d = room.userDisplay) == null ? void 0 : _d.randomIdentities).avatar;
  } else if ((_e = room.userDisplay) == null ? void 0 : _e.randomAvatars) {
    return selectFromList(info.id, room.userDisplay.randomAvatars);
  } else {
    return defaultAvatar;
  }
};
var roomDisplayName = (info, room) => {
  var _a2, _b, _c, _d, _e, _f;
  if ((_a2 = room.userDisplay) == null ? void 0 : _a2.identities) {
    return room.userDisplay.identities[info.id].name || selectFromList(info.id, names);
  } else if ((_b = room.userDisplay) == null ? void 0 : _b.names) {
    return room.userDisplay.names[info.id] || selectFromList(info.id, names);
  } else if ((_c = room.userDisplay) == null ? void 0 : _c.randomIdentities) {
    return selectFromList(info.id, (_d = room.userDisplay) == null ? void 0 : _d.randomIdentities).name;
  } else if ((_e = room.userDisplay) == null ? void 0 : _e.randomNames) {
    return selectFromList(info.id, (_f = room.userDisplay) == null ? void 0 : _f.randomNames);
  } else {
    return selectFromList(info.id, names);
  }
};
var avatarUrl = function (info, room, defaultAvatar) {
  if (defaultAvatar === void 0) {
    defaultAvatar = DEFAULT_AVATAR;
  }
  var _a2;
  if (info.avatar && !((_a2 = room.access) == null ? void 0 : _a2.lockedIdentities)) {
    return info.avatar;
  } else {
    return roomAvatar(info, room, defaultAvatar);
  }
};
var displayName = (info, room) => {
  var _a2;
  const infoName = info.name || info.displayName;
  if (infoName && !((_a2 = room.access) == null ? void 0 : _a2.lockedIdentities)) {
    return infoName;
  } else if (room) {
    return roomDisplayName(info, room);
  } else {
    return selectFromList(info.id, names);
  }
};
var selectFromList = (id, list) => {
  return list[publicKeyToIndex(id, list.length)];
};
var names = ["Ali", "Alex", "Ash", "Blue", "Chi", "Drew", "Eight", "Fin", "Floor", "Five", "Four", "Jam", "Jaz", "Misha", "Mu", "Nine", "One", "Pat", "Sam", "Sasha", "Seven", "Six", "Sky", "Sol", "Storm", "Sun", "Tao", "Ten", "Three", "Tsu", "Two", "Yu", "Zero"];
var integerFromBytes = rawBytes => rawBytes[0] + (rawBytes[1] << 8) + (rawBytes[2] << 16) + (rawBytes[3] << 24);
function publicKeyToIndex(publicKey, range) {
  const bytes = decode(publicKey);
  return Math.abs(integerFromBytes(bytes)) % range;
}

// ../../lib/animate-emoji.js
var g = -4e-5;
var G = Math.abs(g);
var tmax = 5e3;
var Rx = 2 * G;
var Fx = 4 * G;
var vx0 = 0.1 * tmax * G;
var Fy = 2 * G;
var vy0 = 0.4 * tmax * G;
var size = 80;
var Ra = 1e-8 * size * 360;
var Fa = 5e-4;
var Sa = 5 * G / size;
function move(t, x, vx, y, vy, a, va) {
  let dt = Date.now() - t;
  t += dt;
  x += dt * vx;
  y += dt * vy;
  a += dt * va;
  vx += dt * (Rx * randn() - Fx * vx);
  vy += dt * (g - Fy * vy);
  va += dt * (-Sa * a + Ra * randn() - Fa * va);
  return [t, x, vx, y, vy, a, va];
}
function render(emojiStyle, t, t0, tend, x, y, a) {
  let timeFraction = (t - t0) / (tend - t0);
  let scale = 1 - 0.75 * timeFraction;
  let opacity = 1 - timeFraction ** 2;
  emojiStyle.opacity = `${opacity}`;
  emojiStyle.transform = `scale(${scale}) translate(${x / scale}px,${-y / scale}px) rotate(${a}deg)`;
}
function animateEmoji(element) {
  let emojiStyle = element.style;
  let t0 = Date.now();
  let tend = t0 + tmax;
  let vx00 = vx0 * randn();
  let [t, x, vx, y, vy, a, va] = [t0, 0, vx00, 0, vy0, 0, 0];
  function step() {
    if (t > tend) return;
    [t, x, vx, y, vy, a, va] = move(t, x, vx, y, vy, a, va);
    render(emojiStyle, t, t0, tend, x, y, a);
    window.requestAnimationFrame(step);
  }
  window.requestAnimationFrame(step);
}
function randn() {
  return Math.sqrt(-2 * Math.log(1 - Math.random())) * Math.cos(2 * Math.PI * Math.random());
}

// ../../jam-core-react/components/v1/Participant.jsx
import { use as use4 } from "use-minimal-state";

// ../../jam-core-react/components/v1/Svg.jsx
import React3 from "react";
function MicOffSvg(_ref8) {
  let {
    stroke,
    ...props
  } = _ref8;
  return /* @__PURE__ */React3.createElement("svg", {
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    width: "96.666px",
    height: "96.666px",
    viewBox: "0 0 96.666 96.666",
    fill: "currentColor",
    xmlSpace: "preserve",
    stroke: stroke || "#4B5563",
    style: {
      transform: "scale(-1,1)"
    },
    ...props
  }, /* @__PURE__ */React3.createElement("g", null, /* @__PURE__ */React3.createElement("g", null, /* @__PURE__ */React3.createElement("path", {
    d: "M65.595,56.035V43.349L38.639,70.307c2.766,1.885,6.104,2.989,9.695,2.989C57.852,73.296,65.595,65.553,65.595,56.035z"
  }), /* @__PURE__ */React3.createElement("path", {
    d: "M76.078,45.715H72.64c-1.104,0-2,0.896-2,2v7.029c0,12.3-10.007,22.308-22.308,22.308c-4.654,0-8.979-1.435-12.559-3.882\n                      l-5.245,5.245c4.037,3.084,8.856,5.177,14.086,5.835v4.98h-15.35c-1.104,0-2,0.896-2,2v3.436c0,1.104,0.896,2,2,2h38.138\n                      c1.104,0,2-0.896,2-2V91.23c0-1.104-0.896-2-2-2H52.051v-4.98c14.594-1.838,26.026-14.799,26.026-29.506v-7.029\n                      C78.078,46.61,77.182,45.715,76.078,45.715z"
  }), /* @__PURE__ */React3.createElement("path", {
    d: "M85.972,7.694c-2.146-2.147-5.631-2.147-7.777,0l-12.6,12.6v-3.032C65.595,7.743,57.852,0,48.333,0\n                      c-9.519,0-17.262,7.743-17.262,17.262v37.554l-4.552,4.552c-0.317-1.493-0.494-3.038-0.494-4.624v-7.029c0-1.104-0.896-2-2-2\n                      h-3.437c-1.104,0-2,0.896-2,2v7.029c0,3.67,0.726,7.227,2.022,10.533l-9.917,9.916c-2.148,2.148-2.148,5.631,0,7.779\n                      c1.073,1.073,2.481,1.61,3.889,1.61s2.815-0.537,3.889-1.61l67.5-67.5C88.121,13.324,88.121,9.842,85.972,7.694z"
  }))));
}

// ../../jam-core-react/components/v1/Participant.jsx
var reactionEmojis = ["\u2764\uFE0F", "\u{1F4AF}", "\u{1F602}", "\u{1F605}", "\u{1F633}", "\u{1F914}"];
var getInfo = (peerId, state) => {
  let [identities, myId, myIdentity] = use4(state, ["identities", "myId", "myIdentity"]);
  return peerId === myId ? myIdentity.info : identities[peerId] || {
    id: peerId
  };
};
function DisplayName(_ref9) {
  let {
    peerId
  } = _ref9;
  const [state] = useJam();
  let [room] = use4(state, ["room"]);
  let info = getInfo(peerId, state);
  return /* @__PURE__ */React4.createElement("span", {
    className: "display-name"
  }, displayName(info, room));
}
function getInitials(peerDisplayName) {
  if (!peerDisplayName || peerDisplayName.length === 0) {
    return "NN";
  }
  const initials = peerDisplayName.split(" ").map(n => n.substring(0, 1));
  if (initials.length === 1) {
    return initials[0];
  }
  const {
    0: first,
    length: l,
    [l - 1]: last
  } = initials;
  return `${first}${last}`;
}
function Avatar(_ref10) {
  let {
    canSpeak,
    peerId,
    speakerRing = false,
    useInitialsAsDefault = true,
    videoAvatar = false
  } = _ref10;
  var _a2;
  const [state, api] = useJam();
  let [myId, room, peerState, reactions, myVideo, remoteVideoStreams] = use4(state, ["myId", "room", "peerState", "reactions", "myVideo", "remoteVideoStreams"]);
  let speaking = speakerRing ? use4(state, "speaking") : new Map();
  let micMuted = peerId === myId ? use4(state, "micMuted") : peerState == null ? void 0 : peerState.micMuted;
  let participantReactions = reactions[peerId];
  let info = getInfo(peerId, state);
  let isSpeaking = speakerRing && speaking.has(peerId);
  const peerAvatarUrl = avatarUrl(info, room, useInitialsAsDefault ? "__no_avatar__" : void 0);
  const peerDisplayName = displayName(info, room);
  const videoStreamRef = /*#__PURE__*/React4.createRef();
  const videoStream = peerId === myId ? myVideo : (_a2 = remoteVideoStreams == null ? void 0 : remoteVideoStreams.find(so => so.peerId === peerId)) == null ? void 0 : _a2.stream;
  useEffect(() => {
    if (videoAvatar && videoStreamRef.current) videoStreamRef.current.srcObject = videoStream;
  }, [videoStream, videoStreamRef]);
  return /* @__PURE__ */React4.createElement("div", {
    key: peerId,
    title: peerDisplayName,
    className: "avatar"
  }, speakerRing && /* @__PURE__ */React4.createElement("div", {
    className: `speaker-ring ${isSpeaking ? "active" : ""}`
  }), /* @__PURE__ */React4.createElement("div", {
    className: "avatar-image"
  }, videoAvatar && videoStream ? /* @__PURE__ */React4.createElement("video", {
    className: "avatar-image-content",
    ref: videoStreamRef,
    autoPlay: true,
    playsInline: true
  }) : peerAvatarUrl !== "__no_avatar__" ? /* @__PURE__ */React4.createElement("img", {
    className: "avatar-image-content",
    alt: peerDisplayName,
    src: peerAvatarUrl
  }) : /* @__PURE__ */React4.createElement("svg", {
    className: "avatar-image-content",
    xmlns: "http://www.w3.org/2000/svg",
    x: "0px",
    y: "0px",
    width: "96.666px",
    height: "96.666px",
    viewBox: "0 0 96.666 96.666",
    fill: "currentColor"
  }, /* @__PURE__ */React4.createElement("text", {
    style: {
      font: "40px sans-serif"
    },
    x: "50%",
    y: "50%",
    textAnchor: "middle",
    dominantBaseline: "middle"
  }, getInitials(peerDisplayName))), /* @__PURE__ */React4.createElement(Reactions, {
    reactions: participantReactions
  })), (!!micMuted || !canSpeak) && /* @__PURE__ */React4.createElement("div", {
    className: "mic-muted"
  }, /* @__PURE__ */React4.createElement(MicOffSvg, {
    fill: !canSpeak ? "red" : void 0
  })));
}
function Reactions(_ref11) {
  let {
    reactions
  } = _ref11;
  if (!reactions) return null;
  return /* @__PURE__ */React4.createElement(React4.Fragment, null, reactions.map(_ref12 => {
    let [r, id] = _ref12;
    return reactionEmojis.includes(r) && /* @__PURE__ */React4.createElement(AnimatedEmoji, {
      key: id,
      emoji: r
    });
  }));
}
function AnimatedEmoji(_ref13) {
  let {
    emoji,
    ...props
  } = _ref13;
  let [element, setElement] = useState(null);
  useEffect(() => {
    if (element) animateEmoji(element);
  }, [element]);
  return /* @__PURE__ */React4.createElement("div", {
    ref: setElement,
    className: "animated-emoji"
  }, emoji);
}

// ../../jam-core-react.ts
var components = {
  v1: {
    Avatar,
    DisplayName
  }
};
export { JamProvider, components, use5 as use, useApiQuery, useJam, useJamState };